// ---------- la première feuille ----------
// Ce qu'un nouveau joueur peint en ouvrant l'application : l'œil d'une tête de chien, découpé
// dans la feuille entière (src/piece.ts). Le bilan qui l'a décidée, le choix du chien contre le
// chat et des dix couleurs sont dans IDEES.md et dans les commentaires de piece.ts ; la grille
// et son identité au serveur, dans src/introGrid.ts ; ici, le moment où on l'ouvre.

import { currentUser } from './account';
import { t } from './i18n';
import { INTRO_ID, INTRO_ZONES, introSheet } from './introGrid';
import { noteIntro } from './introStats';
import { openPiece, pieceDone, type PieceRect } from './piece';
import { flushProgress, loadProgress, progressIndex, resetProgress } from './progress';

export { INTRO_ID, INTRO_ZONES, introSheet };

/** L'œil gauche du chien, 38 cases de côté : la plus grande pièce sous 450 zones. */
export const INTRO_RECT: PieceRect = { r0: 27, c0: 17, w: 38, h: 38 };

/**
 * Ce qu'il faut ouvrir au lancement :
 * - `piece` : un NOUVEL appareil — aucune progression et aucun compte —, ou la pièce de l'œil
 *   commencée et pas finie alors que c'est sa SEULE feuille (il a fermé l'application au milieu :
 *   il la retrouve là où il l'a laissée). Un joueur qui a quitté l'œil pour jouer autre chose a
 *   choisi : la pièce ne lui est plus imposée — elle l'était à chaque lancement jusqu'au
 *   2026-10-31 ;
 * - `big` : la pièce est finie mais pas la feuille, et c'est sa SEULE feuille. Il a vu le recul
 *   et continué le chien ; tant qu'il n'a rien ouvert d'autre, c'est là qu'il revient ;
 * - null : un joueur qui a déjà autre chose — le lancement ordinaire.
 *
 * « Aucun compte » et pas seulement « aucune progression » (Vincent, 2026-10-31) : un appareil qui a
 * un jeton a déjà joué — un compte ne se crée qu'à la première sauvegarde (cf. ensureUser).
 *
 * CE QUE CETTE GARDE NE COUVRE PAS, et il faut le savoir. Le jeton et la progression vivent au
 * même endroit (le stockage de la WebView) : une sauvegarde Android les restaure ensemble, un
 * téléphone neuf n'a ni l'un ni l'autre. Le joueur existant qui change de téléphone voit donc
 * l'initiation une fois, avant d'entrer son code de reprise — c'est l'appareil qui est nouveau, et
 * rien ici ne peut savoir qui le tient. Ce que la garde évite, c'est l'appareil qui a perdu ses
 * sauvegardes mais gardé son compte.
 */
export async function introStep(): Promise<'piece' | 'big' | null> {
  if (!introEnabled()) return null;
  const index = progressIndex();
  if (index.size === 0) return (await currentUser()) ? null : 'piece';
  // Le chien n'est pas la seule feuille : le joueur est passé à autre chose, quoi qu'il en reste.
  if (index.size > 1) return null;
  const s = introSheet();
  const saved = loadProgress(INTRO_ID, s.fp);
  if (!saved || saved.done) return null;
  return pieceDone(s.target, INTRO_RECT, saved.taps, s.gridW) ? 'big' : 'piece';
}

/** L'initiation existe-t-elle sur ce build ? Partout depuis que le chien a son identité au serveur
 *  (cf. l'en-tête) ; gardé en fonction parce que c'est aussi ce qui montre « Revoir l'initiation »
 *  dans les réglages — la débrancher d'une ligne les retire ensemble. */
export function introEnabled(): boolean {
  return true;
}

/**
 * « Revoir l'initiation », dans les réglages : le chien repart de zéro, et l'œil s'ouvre comme
 * au premier lancement. De zéro et non là où il en est : une pièce déjà finie ne se rouvre pas
 * (openPiece rend faux), et c'est l'ENCHAÎNEMENT qu'on veut revoir — l'œil, le recul, le renard.
 * Ce que le joueur avait peint du chien entier est perdu ; c'est le prix d'une initiation qu'on
 * rejoue, et la rangée ne promet pas autre chose.
 */
export function restartIntro(onLibrary: () => void): boolean {
  const s = introSheet();
  // Une écriture en attente appartient à la feuille qu'on quitte, et resetProgress la jette :
  // elle part d'abord.
  flushProgress();
  resetProgress(INTRO_ID, s.fp);
  return openIntroPiece(onLibrary);
}

/** Ouvre la pièce de l'œil, en reprenant ce qui en a déjà été peint. Ne ferme pas le catalogue :
 *  c'est à l'appelant (main.ts). */
/** `onLibrary` : « Voir la bibliothèque » dans le dialogue de fin du renard (cf. PieceOptions). */
export function openIntroPiece(onLibrary: () => void): boolean {
  const s = introSheet();
  const saved = loadProgress(INTRO_ID, s.fp);
  const opened = openPiece(s, INTRO_RECT, saved?.taps ?? [], {
    ghost: true,
    line: t('piece.line'),
    onLibrary,
    onStep: noteIntro,
  });
  if (opened) noteIntro('opened');
  return opened;
}
